import { useTranslation } from '@gem/modules/i18n';

import {
  groupAliasCreateValidation,
  groupAliasEditValidation,
  groupNameValidation,
} from '../fields/fields';
import type { IFormFieldValidation, IValidateParams, ValidationResult } from '../hooks';
import { useAsyncValidation, useSyncValidation } from '../hooks';
import { ValidationErrorType } from '../validation.types';

export interface IGroupAliasAsyncDeps {
  groupAliasAsyncValidation: (params: IValidateParams) => Promise<string | null>;
}

export interface IChatGroupValidations {
  groupName: IFormFieldValidation;
  groupAliasCreate: IFormFieldValidation;
  groupAliasEdit: IFormFieldValidation;
}

export const useSharedChatGroupValidations = (
  deps: IGroupAliasAsyncDeps
): IChatGroupValidations => {
  const { t } = useTranslation();

  const groupNameErrorToText = {
    [ValidationErrorType.Empty]: ' ',
    [ValidationErrorType.TooShort]: t('username_min_length_error'),
  };

  const groupAliasErrorToText = {
    [ValidationErrorType.Empty]: ' ',
    [ValidationErrorType.TooShort]: t('username_min_length_error'),
    [ValidationErrorType.IncorrectSymbols]: t('channel_alias_allowed_symbols_error'),
    [ValidationErrorType.AlreadyExists]: t('channel_link_is_not_available'),
  };

  const groupNameValidator = useSyncValidation();
  const groupAliasValidator = useAsyncValidation();

  return {
    groupName: {
      validate: (value, _formData, meta): ValidationResult => {
        return groupNameValidator({
          value,
          meta: meta!,
          validate: groupNameValidation,
          errorToText: groupNameErrorToText,
        });
      },
    },
    groupAliasCreate: {
      validate: (value, formData, meta): ValidationResult => {
        return groupAliasValidator({
          value,
          values: formData,
          meta: meta!,
          validate: groupAliasCreateValidation,
          asyncValidate: deps.groupAliasAsyncValidation,
          errorToText: groupAliasErrorToText,
        });
      },
    },
    groupAliasEdit: {
      validate: (value, formData, meta): ValidationResult => {
        return groupAliasValidator({
          value,
          values: formData,
          meta: meta!,
          validate: groupAliasEditValidation,
          asyncValidate: deps.groupAliasAsyncValidation,
          errorToText: groupAliasErrorToText,
        });
      },
    },
  };
};
